// ── Role constants — single source of truth for all RBAC decisions ──────────
// No hard-coded role strings anywhere else in the codebase.

import type { UserRole } from '../types';

export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  VIEWER: 'viewer',
  DIST_REP: 'dist_rep',
} as const satisfies Record<string, UserRole>;

// Pages each role can access. Key = route path prefix. Value = roles allowed (any match = allowed).
// Admin always has access to everything — checked first, no need to list.
// canAccess() uses longest-prefix match, so more specific child routes
// (e.g. '/purchasing/receiving') override their parent grants — the order
// of keys in this object is irrelevant to correctness.
export const PAGE_ACCESS: Record<string, UserRole[]> = {
  '/dashboard': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  '/tasks': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  // /files: admin + user + dist_rep (scoped). B18 resolved 2026-04-28 by
  // migration 20260428000001_files_distributor_id.sql which adds files.distributor_id
  // (backfilled from related_po_id → purchase_orders.distributor_id) and replaces
  // files_read_admin_user with files_read_role_scoped — dist_rep reads only files
  // where distributor_id = auth_distributor_id(). Files.tsx hides upload + mutation
  // affordances for dist_rep (read-only).
  '/files': [ROLES.USER, ROLES.DIST_REP],
  '/catalog': [ROLES.USER, ROLES.VIEWER],
  // /purchasing parent: USER + VIEWER (V-ROUI Day 64.5). dist_rep sees Receiving
  // and Discrepancies via the explicit child entries below — never Buying
  // Session, PO Builder, or Purchase Orders (Architecture Rule 4 — dist_rep
  // excluded from pricing and margin data). B12 fix: 2026-04-23. Viewer sees
  // all Purchasing surfaces with mutation UI hidden/disabled (V-ROUI per
  // DOMAIN_ARCHITECTURE §5).
  '/purchasing/receiving': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  '/purchasing/discrepancies': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  '/purchasing': [ROLES.USER, ROLES.VIEWER],
  '/inventory': [ROLES.USER, ROLES.VIEWER],
  '/inventory/restock': [ROLES.USER, ROLES.VIEWER],
  '/listings': [ROLES.USER, ROLES.VIEWER],
  '/fulfillment': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  '/returns': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  // /transfers parent: USER + VIEWER (V-ROUI Day 64.5). dist_rep sees Removals
  // (scoped read-only per DOMAIN_ARCHITECTURE §5) but not Inbound. B15 fix:
  // 2026-04-23. Viewer sees both Inbound + Removals with mutation UI gated.
  '/transfers/removals': [ROLES.USER, ROLES.VIEWER, ROLES.DIST_REP],
  '/transfers': [ROLES.USER, ROLES.VIEWER],
  '/finance': [], // admin only — empty = no non-admin access
  '/reports': [], // admin only
  '/directory': [ROLES.USER, ROLES.VIEWER],
  '/settings': [ROLES.USER], // Settings sub-pages further gated below
};

// Settings sub-sections — admin only (hidden for non-admin users).
// Day 64 A.4: 'params' added (Params & Defaults now admin-only).
// Day 64 A.5: 'marketplace-toggle' + 'fulfillment-paths' removed — sections retired
// Day 63a (graduated to arbi-dev Formula Editor). 'error-log' also removed; the
// SectionCard's own (admin && DEV) self-gate at Settings.tsx:1369 + the standalone
// DEV-only nav filter at Settings.tsx:2015 jointly keep PROD nav hidden and
// DEV-mode non-admin from reaching the Sentry trigger button.
// Day 99b.2: 'users' removed — graduated to arbi-dev /admin/users (User Management).
export const ADMIN_ONLY_SETTINGS: readonly string[] = [
  'security',
  'po-types',
  'rules-engine',
  'api-keys',
  'audit-trail',
  'data',
  'params',
  // Day 86 P7.2 (A1 + #DBN-R1-β) — Channels & Paths Settings section. Admin-only
  // per Architecture Rule 4 (channel + path activation is engine-relevant policy).
  'channels-and-paths',
  // Day 87 P7.3 + D.8 — Cost Line Items Settings section. Admin-only per
  // Architecture Rule 4 (cost components are engine-relevant policy).
  'cost-line-items',
  // Day 89 P7.4 — Distributor Scope Settings section. Admin-only per
  // Architecture Rule 4 (per-nav-domain scope is engine-relevant policy).
  // Inverts D.14 A10 stub-removal disposition under Nav Addendum §3d.
  'distributor-scope',
  // U10 A2 (BACKLOG:315 [ANNOTATED AR-0145: DEAD at HEAD; anchor by content — "U10 A2 - the per-distributor capability registry editor"]a) — Distributor Capabilities Settings section.
  // Admin-only per Architecture Rule 4 (capability-vs-fallback gating is
  // engine-relevant policy; DB writes are is_admin()-RLS'd — the FE gate
  // mirrors the matrix's write surface, narrower than its admin/user/viewer
  // SELECT, per the standing Rule-4 idiom).
  'distributor-capabilities',
  // Fix Pass A (S-F-T3.6-10) — Marketplace APIs integration stub. Admin-only per
  // Architecture Rule 4, consistent with every other integration scaffold; the
  // stub previously rendered unguarded (nav-visible + panel-shown to non-admins).
  'marketplace-apis',
  // Day 93 P7.7 — Settings Stub Sections Round 2 scaffolds. All 9 added
  // admin-only per Architecture Rule 4 — integration credentials + webhook
  // endpoint config + template content + billing read are admin-scope
  // policy regardless of bucket (a-integration_config / b-dedicated /
  // c-inert). rules-engine already present at L65.
  'repricer',
  'blvs-api',
  'keepa',
  'docs-templates',
  'billing',
  'multichannel',
  'shipping',
  'accounting',
  'webhooks',
];

/**
 * Check if a role can access a given path.
 * Admin always returns true. Otherwise checks PAGE_ACCESS using
 * longest-prefix matching so more specific entries (e.g.
 * '/purchasing/receiving') override their parent (e.g. '/purchasing').
 */
export function canAccess(role: UserRole | null | undefined, path: string): boolean {
  if (role === ROLES.ADMIN) return true;
  const match = Object.keys(PAGE_ACCESS)
    .filter((prefix) => path.startsWith(prefix))
    .sort((a, b) => b.length - a.length)[0];
  if (!match) return false;
  return role ? PAGE_ACCESS[match].includes(role) : false;
}

/**
 * Check if a settings section is admin-only.
 */
export function isAdminOnlySection(sectionId: string): boolean {
  return ADMIN_ONLY_SETTINGS.includes(sectionId);
}
